import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { WifiIcon, BluetoothIcon, SunIcon, Volume2Icon, MoonIcon, PlaneIcon, KeyboardIcon } from 'lucide-react';
interface ControlCenterProps {
  isOpen: boolean;
  onClose: () => void;
  brightness: number;
  setBrightness: (v: number) => void;
  volume: number;
  setVolume: (v: number) => void;
  darkMode: boolean;
  setDarkMode: (v: boolean) => void;
  wifi: boolean;
  setWifi: (v: boolean) => void;
  bluetooth: boolean;
  setBluetooth: (v: boolean) => void;
  airplane: boolean;
  setAirplane: (v: boolean) => void;
}
export const ControlCenter: React.FC<ControlCenterProps> = ({
  isOpen,
  onClose,
  brightness,
  setBrightness,
  volume,
  setVolume,
  darkMode,
  setDarkMode,
  wifi,
  setWifi,
  bluetooth,
  setBluetooth,
  airplane,
  setAirplane
}) => {
  return <AnimatePresence data-id="element-248" data-mp="2xh9bh3uugk5i">
      {isOpen && <>
          {/* Click outside catcher */}
          <div className="fixed inset-0 z-[60]" onClick={onClose} data-id="element-249" data-mp="2snn5fuj6reio" />

          <motion.div initial={{
        opacity: 0,
        y: -10,
        scale: 0.97
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: -10,
        scale: 0.97
      }} transition={{
        type: 'spring',
        damping: 22,
        stiffness: 280
      }} className="fixed top-9 right-4 z-[70] w-80 bg-white/10 backdrop-blur-2xl border border-white/15 rounded-2xl shadow-2xl p-4 text-white" style={{
        backgroundColor: 'rgba(30, 30, 32, 0.7)'
      }} data-id="element-250" data-mp="uxigxosn6xj5">
            {/* Top row: Wi-Fi / Bluetooth / Airplane */}
            <div className="bg-white/5 rounded-xl p-3 mb-3 grid grid-cols-3 gap-2" data-id="element-251" data-mp="1wmujcny303dt">
              <ToggleTile icon={<WifiIcon size={18} data-id="element-253" data-mp="21kw1n6nonyo8" />} label="Wi-Fi" sub="Home" active={wifi} onClick={() => setWifi(!wifi)} data-id="element-252" data-mp="21lxcpb4p8l5y" />
              <ToggleTile icon={<BluetoothIcon size={18} data-id="element-255" data-mp="1v4nge3w7ppw0" />} label="Bluetooth" sub={bluetooth ? 'On' : 'Off'} active={bluetooth} onClick={() => setBluetooth(!bluetooth)} data-id="element-254" data-mp="1v5p2d3ernwsi" />
              <ToggleTile icon={<PlaneIcon size={18} data-id="element-257" data-mp="2iuht8lgj7eg9" />} label="AirDrop" sub={airplane ? 'On' : 'Off'} active={airplane} onClick={() => setAirplane(!airplane)} data-id="element-256" data-mp="2ivjf7kz35lcr" />
            </div>

            {/* Dark mode + Keyboard row */}
            <div className="bg-white/5 rounded-xl p-3 mb-3 grid grid-cols-2 gap-2" data-id="element-258" data-mp="24755qr75yyn8">
              <ToggleTile icon={darkMode ? <MoonIcon size={18} data-id="element-260" data-mp="31msrg208qo81" /> : <SunIcon size={18} data-id="element-261" data-mp="31o3ufzlv3ry3" />} label={darkMode ? 'Dark Mode' : 'Light Mode'} sub={darkMode ? 'On' : 'Off'} active={darkMode} onClick={() => setDarkMode(!darkMode)} data-id="element-259" data-mp="31lr4az5yab9y" />
              <ToggleTile icon={<KeyboardIcon size={18} data-id="element-263" data-mp="3961yvpp6scug" />} label="Keyboard" sub="Brightness" active={false} onClick={() => {}} data-id="element-262" data-mp="394qmx2ll7ys9" />
            </div>

            {/* Display brightness */}
            <div className="bg-white/5 rounded-xl p-3 mb-3" data-id="element-264" data-mp="3n5kuxk4q3xqe">
              <div className="text-xs font-semibold text-white/70 mb-2 flex items-center gap-2" data-id="element-265" data-mp="3n51gwei71thn">
                <SunIcon size={12} data-id="element-266" data-mp="3s2l629gfjqod" /> Display
              </div>
              <Slider value={brightness} onChange={setBrightness} icon={<SunIcon size={14} data-id="element-268" data-mp="5ns0wmyl60b5" />} data-id="element-267" data-mp="qrqjzupebe8" />
            </div>

            {/* Sound */}
            <div className="bg-white/5 rounded-xl p-3" data-id="element-269" data-mp="aoxrae4rxbhi">
              <div className="text-xs font-semibold text-white/70 mb-2 flex items-center gap-2" data-id="element-270" data-mp="ephpzm4zwn5r">
                <Volume2Icon size={12} data-id="element-271" data-mp="1cee8f5h15nq2" /> Sound
              </div>
              <Slider value={volume} onChange={setVolume} icon={<Volume2Icon size={14} data-id="element-273" data-mp="17errom1ztpci" />} data-id="element-272" data-mp="1ccjljlm77ljc" />
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
};
const ToggleTile: React.FC<{
  icon: React.ReactNode;
  label: string;
  sub: string;
  active: boolean;
  onClick: () => void;
}> = ({
  icon,
  label,
  sub,
  active,
  onClick
}) => <button onClick={onClick} className={`flex items-center gap-2 p-2 rounded-lg transition-all hover:-translate-y-0.5 ${active ? 'bg-blue-500 text-white' : 'bg-white/5 text-white/80 hover:bg-white/10'}`} data-id="element-274" data-mp="thmg66jify9v">
    <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${active ? 'bg-white/20' : 'bg-white/10'}`} data-id="element-275" data-mp="12du4fv24m545">
      {icon}
    </div>
    <div className="text-left min-w-0" data-id="element-276" data-mp="2dtshea0fdban">
      <div className="text-[11px] font-semibold truncate" data-id="element-277" data-mp="28waeb0939qak">{label}</div>
      <div className="text-[10px] opacity-70 truncate" data-id="element-278" data-mp="28yd53mi2fngc">{sub}</div>
    </div>
  </button>;
const Slider: React.FC<{
  value: number;
  onChange: (v: number) => void;
  icon: React.ReactNode;
}> = ({
  value,
  onChange,
  icon
}) => <div className="relative h-6 bg-white/10 rounded-full overflow-hidden flex items-center" data-id="element-279" data-mp="27f3er4i31pnd">
    <div className="absolute left-0 top-0 bottom-0 bg-white/80 transition-all" style={{
    width: `${value}%`
  }} data-id="element-280" data-mp="27hg5o75tjack" />
    <div className="relative z-10 flex items-center w-full px-2 text-mac-dark" data-id="element-281" data-mp="304tufhzd4zll">
      <div className="opacity-80" data-id="element-282" data-mp="353dhxmtlujg2">{icon}</div>
    </div>
    <input type="range" min={20} max={100} value={value} onChange={e => onChange(Number(e.target.value))} className="absolute inset-0 opacity-0 cursor-pointer" data-id="element-283" data-mp="3522hnwzdbttp" />
  </div>;